import { Link, useLocation } from "react-router-dom";
import { Phone, CalendarCheck } from "lucide-react";
import { useState, useRef, useEffect, useCallback } from "react";

const HIDE_ON = ["/book", "/login", "/admin", "/technician", "/partner"];
const STORAGE_KEY = "floatingCTAPos";
const DRAG_THRESHOLD = 6;

const WA_PATH =
  "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z";

function WaIcon({ size = 20 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className="fill-current"
      aria-hidden="true"
    >
      <path d={WA_PATH} />
    </svg>
  );
}

export default function FloatingCTA() {
  const { pathname } = useLocation();
  const [pos, setPos] = useState(null);
  const fabRef = useRef(null);
  const drag = useRef({
    active: false,
    ox: 0,
    oy: 0,
    startX: 0,
    startY: 0,
    moved: false,
  });
  const posRef = useRef(null);

  // Keep posRef in sync so window listeners don't close over stale pos
  useEffect(() => {
    posRef.current = pos;
  }, [pos]);

  // Restore saved position on mount — validate against current viewport so an
  // off-screen position from a previous session doesn't hide the FAB.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (
        saved &&
        typeof saved.left === "number" &&
        typeof saved.top === "number"
      ) {
        const maxLeft = window.innerWidth - 68;
        const maxTop = window.innerHeight - 200;
        if (
          saved.left >= 4 &&
          saved.left <= maxLeft &&
          saved.top >= 4 &&
          saved.top <= maxTop
        ) {
          setPos(saved);
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch {}
  }, []);

  // Window-level listeners so drag works even when cursor leaves the element,
  // and avoids setPointerCapture which redirects click targets away from buttons.
  useEffect(() => {
    const onMove = (e) => {
      if (!drag.current.active) return;
      const dx = Math.abs(e.clientX - drag.current.startX);
      const dy = Math.abs(e.clientY - drag.current.startY);
      if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) drag.current.moved = true;
      if (!drag.current.moved) return;

      const el = fabRef.current;
      if (!el) return;
      const newLeft = Math.max(
        4,
        Math.min(
          window.innerWidth - el.offsetWidth - 4,
          e.clientX - drag.current.ox,
        ),
      );
      const newTop = Math.max(
        4,
        Math.min(
          window.innerHeight - el.offsetHeight - 4,
          e.clientY - drag.current.oy,
        ),
      );
      setPos({ left: newLeft, top: newTop });
    };

    const onUp = () => {
      if (!drag.current.active) return;
      drag.current.active = false;
      if (drag.current.moved && posRef.current) {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(posRef.current));
        } catch {}
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const onPointerDown = useCallback((e) => {
    const rect = fabRef.current.getBoundingClientRect();
    drag.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      ox: e.clientX - rect.left,
      oy: e.clientY - rect.top,
      moved: false,
    };
    // No setPointerCapture — keeping it off ensures click events fire on the
    // actual button/link target, not on this container.
  }, []);

  const guardClick = useCallback((e) => {
    if (drag.current.moved) e.preventDefault();
  }, []);

  if (HIDE_ON.some((p) => pathname.startsWith(p))) return null;

  const fabStyle = pos
    ? { left: pos.left, top: pos.top }
    : { bottom: "5rem", right: "1rem" };

  return (
    /* ── Draggable FAB stack (all screen sizes, right-anchored) ── */
    <div
      ref={fabRef}
      className="fixed z-40 flex flex-col items-center gap-2 touch-none select-none"
      style={fabStyle}
      onPointerDown={onPointerDown}
    >
      {/* Drag handle */}
      <div className="flex gap-0.5 mb-0.5 cursor-grab active:cursor-grabbing">
        {[0, 1, 2].map((i) => (
          <div key={i} className="w-1 h-1 rounded-full bg-neutral-400/70" />
        ))}
      </div>

      {/* Book Now */}
      <Link
        to="/book"
        onClick={guardClick}
        aria-label="Book Now"
        className="w-12 h-12 flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg shadow-primary-900/50 transition-colors"
      >
        <CalendarCheck size={20} />
      </Link>

      {/* WhatsApp */}
      <a
        href="https://wa.me/918394888822?text=Hi%2C%20I%20want%20to%20book%20a%20car%20service"
        target="_blank"
        rel="noopener noreferrer"
        onClick={guardClick}
        aria-label="WhatsApp"
        className="w-12 h-12 flex items-center justify-center bg-green-500 hover:bg-green-600 text-white rounded-full shadow-lg transition-colors"
      >
        <WaIcon size={20} />
      </a>

      {/* Call */}
      <a
        href="tel:+918394888822"
        onClick={guardClick}
        aria-label="Call Us"
        className="w-12 h-12 flex items-center justify-center bg-neutral-800 hover:bg-neutral-700 text-white rounded-full shadow-lg transition-colors"
      >
        <Phone size={18} />
      </a>
    </div>
  );
}
